import React, { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../api/api';
import DocumentUpload from './DocumentUpload';
import './KYCForm.css';

const KYCForm = ({ merchantId }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [kycData, setKycData] = useState({
    fullName: '',
    businessName: '',
    businessType: 'INDIVIDUAL',
    panNumber: '',
    gstin: '',
    registeredAddress: '',
    city: '',
    state: '',
    pincode: '',
    bankAccountNumber: '',
    bankIfscCode: '',
    bankAccountHolderName: ''
  });
  const [verificationStatus, setVerificationStatus] = useState({
    pan: false,
    gst: false,
    bank: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const businessTypes = [
    'INDIVIDUAL', 'SOLE_PROPRIETORSHIP', 'PARTNERSHIP', 
    'PRIVATE_LIMITED', 'PUBLIC_LIMITED', 'LLP', 'NGO', 'TRUST', 'OTHER'
  ];

  useEffect(() => {
    fetchKYCStatus();
  }, [merchantId]);

  const fetchKYCStatus = async () => {
    try {
      // Use mock data for demo mode
      if (!merchantId) {
        setLoading(false);
        return;
      }

      // Simulate API call with mock data
      await new Promise(resolve => setTimeout(resolve, 300));
      
      const mockKycData = {
        fullName: 'Demo Merchant',
        businessName: 'Demo Business Pvt Ltd',
        businessType: 'Technology',
        panNumber: 'ABCDE1234F',
        gstin: '',
        registeredAddress: '123 Demo Street',
        city: 'Demo City',
        state: 'Demo State',
        pincode: '123456',
        accountHolderName: 'Demo Merchant',
        accountNumber: '',
        ifscCode: '',
        bankName: '',
        status: 'IN_PROGRESS'
      };
      
      const kyc = mockKycData;
      setKycData({
        fullName: kyc.fullName || '',
        businessName: kyc.businessName || '',
        businessType: kyc.businessType || 'INDIVIDUAL',
        panNumber: kyc.panNumber || '',
        gstin: kyc.gstin || '',
        registeredAddress: kyc.registeredAddress || '',
        city: kyc.city || '',
        state: kyc.state || '',
        pincode: kyc.pincode || '',
        bankAccountNumber: kyc.bankAccountNumber || '',
        bankIfscCode: kyc.bankIfscCode || '',
        bankAccountHolderName: kyc.bankAccountHolderName || ''
      });
      setVerificationStatus({
        pan: kyc.panVerified || false,
        gst: kyc.gstinVerified || false,
        bank: kyc.bankVerified || false
      });
    } catch (error) {
      // KYC not initiated yet
      initiateKYC();
    } finally {
      setLoading(false);
    }
  };

  const initiateKYC = async () => {
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));
      console.log('KYC initiated successfully (mock)');
    } catch (error) {
      console.error('Error initiating KYC:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setKycData({ ...kycData, [field]: value });
    setError('');
  };

  const saveCurrentStep = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 800));
      setSuccess('Information saved successfully');
    } catch (error) {
      setError('Failed to save information');
    } finally {
      setLoading(false);
    }
  };

  const verifyPAN = async () => {
    if (!kycData.panNumber || kycData.panNumber.length !== 10) {
      setError('Please enter a valid 10-character PAN number');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      // Simulate PAN verification
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Mock successful verification
      setVerificationStatus({ ...verificationStatus, pan: true });
      setSuccess('PAN verified successfully!');
    } catch (error) {
      setError(error.response?.data?.error || 'PAN verification failed');
    } finally {
      setLoading(false);
    }
  };

  const verifyGST = async () => {
    if (!kycData.gstin || kycData.gstin.length !== 15) {
      setError('Please enter a valid 15-character GSTIN');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      // Simulate GST verification
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Mock successful verification
      setVerificationStatus({ ...verificationStatus, gst: true });
      setSuccess('GSTIN verified successfully!');
    } catch (error) {
      setError(error.response?.data?.error || 'GSTIN verification failed');
    } finally {
      setLoading(false);
    }
  };

  const verifyBank = async () => {
    if (!kycData.bankAccountNumber || !kycData.bankIfscCode || !kycData.bankAccountHolderName) {
      setError('Please fill all bank details');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      // Simulate bank verification
      await new Promise(resolve => setTimeout(resolve, 2500));
      
      // Mock successful verification
      setVerificationStatus({ ...verificationStatus, bank: true });
      setSuccess('Bank account verified successfully!');
    } catch (error) {
      setError(error.response?.data?.error || 'Bank verification failed');
    } finally {
      setLoading(false);
    }
  };

  const submitForReview = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Simulate KYC submission
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Mock successful submission
      setSuccess('KYC submitted for review! You will be notified once approved.');
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 2000);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to submit KYC');
    } finally {
      setLoading(false);
    }
  };

  const nextStep = async () => {
    await saveCurrentStep();
    if (currentStep < 6) {
      setCurrentStep(currentStep + 1);
      setSuccess('');
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setError('');
      setSuccess('');
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="step-content">
            <h4>Step 1: Business Information</h4>
            <div className="form-group">
              <label>Full Name *</label>
              <input
                type="text"
                value={kycData.fullName}
                onChange={(e) => handleInputChange('fullName', e.target.value)}
                className="form-control"
                placeholder="Enter your full name"
              />
            </div>
            <div className="form-group">
              <label>Business Name *</label>
              <input
                type="text"
                value={kycData.businessName}
                onChange={(e) => handleInputChange('businessName', e.target.value)}
                className="form-control"
                placeholder="Enter business name"
              />
            </div>
            <div className="form-group">
              <label>Business Type *</label>
              <select
                value={kycData.businessType}
                onChange={(e) => handleInputChange('businessType', e.target.value)}
                className="form-control"
              >
                {businessTypes.map(type => (
                  <option key={type} value={type}>
                    {type.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="step-content">
            <h4>Step 2: PAN & GST Verification</h4>
            <div className="form-group">
              <label>PAN Number * {verificationStatus.pan && '✅ Verified'}</label>
              <div className="input-with-button">
                <input
                  type="text"
                  value={kycData.panNumber}
                  onChange={(e) => handleInputChange('panNumber', e.target.value.toUpperCase())}
                  className="form-control"
                  placeholder="ABCDE1234F"
                  maxLength="10"
                />
                <button 
                  onClick={verifyPAN}
                  disabled={loading || verificationStatus.pan}
                  className="btn btn-secondary"
                >
                  {verificationStatus.pan ? 'Verified' : 'Verify PAN'}
                </button>
              </div>
            </div>
            <div className="form-group">
              <label>GSTIN (Optional) {verificationStatus.gst && '✅ Verified'}</label>
              <div className="input-with-button">
                <input
                  type="text"
                  value={kycData.gstin}
                  onChange={(e) => handleInputChange('gstin', e.target.value.toUpperCase())}
                  className="form-control"
                  placeholder="22AAAAA0000A1Z5"
                  maxLength="15"
                />
                <button 
                  onClick={verifyGST}
                  disabled={loading || verificationStatus.gst || !kycData.gstin}
                  className="btn btn-secondary"
                >
                  {verificationStatus.gst ? 'Verified' : 'Verify GST'}
                </button>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="step-content">
            <h4>Step 3: Address Information</h4>
            <div className="form-group">
              <label>Registered Address *</label>
              <textarea
                value={kycData.registeredAddress}
                onChange={(e) => handleInputChange('registeredAddress', e.target.value)}
                className="form-control"
                placeholder="Enter complete address"
                rows="3"
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>City *</label>
                <input
                  type="text"
                  value={kycData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label>State *</label>
                <input
                  type="text"
                  value={kycData.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label>Pincode *</label>
                <input
                  type="text"
                  value={kycData.pincode}
                  onChange={(e) => handleInputChange('pincode', e.target.value)}
                  className="form-control"
                  maxLength="6"
                />
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="step-content">
            <h4>Step 4: Bank Account Details {verificationStatus.bank && '✅ Verified'}</h4>
            <div className="form-group">
              <label>Bank Account Number *</label>
              <input
                type="text"
                value={kycData.bankAccountNumber}
                onChange={(e) => handleInputChange('bankAccountNumber', e.target.value)}
                className="form-control"
                placeholder="Enter account number"
              />
            </div>
            <div className="form-group">
              <label>IFSC Code *</label>
              <input
                type="text"
                value={kycData.bankIfscCode}
                onChange={(e) => handleInputChange('bankIfscCode', e.target.value.toUpperCase())}
                className="form-control"
                placeholder="SBIN0001234"
                maxLength="11"
              />
            </div>
            <div className="form-group">
              <label>Account Holder Name *</label>
              <input
                type="text"
                value={kycData.bankAccountHolderName}
                onChange={(e) => handleInputChange('bankAccountHolderName', e.target.value)}
                className="form-control"
                placeholder="As per bank records"
              />
            </div>
            <button 
              onClick={verifyBank}
              disabled={loading || verificationStatus.bank}
              className="btn btn-primary"
            >
              {verificationStatus.bank ? '✅ Bank Verified' : 'Verify Bank Account (Penny Drop)'}
            </button>
          </div>
        );

      case 5:
        return (
          <div className="step-content">
            <h4>Step 5: Upload Documents</h4>
            <p className="step-description">
              Please upload the following documents for verification:
            </p>
            <ul className="required-docs">
              <li>✓ PAN Card (mandatory)</li>
              <li>✓ Address Proof (Aadhaar/Passport)</li>
              <li>✓ Cancelled Cheque or Bank Statement</li>
              <li>○ GST Certificate (if applicable)</li>
              <li>○ Business Registration (if applicable)</li>
            </ul>
            <DocumentUpload merchantId={merchantId} />
            
            <div className="validation-section">
              <h5>🔍 Document Validation</h5>
              <p>Once you've uploaded your documents, you can run advanced validation checks:</p>
              <button 
                className="btn btn-secondary"
                onClick={() => window.location.href = '/kyc/validation'}
                style={{ marginTop: '15px' }}
              >
                🚀 Run Advanced Validation
              </button>
            </div>
          </div>
        );

      case 6:
        return (
          <div className="step-content">
            <h4>Step 6: Review & Submit</h4>
            <div className="review-section">
              <h5>Business Information</h5>
              <div className="review-item">
                <span className="label">Full Name:</span>
                <span className="value">{kycData.fullName}</span>
              </div>
              <div className="review-item">
                <span className="label">Business Name:</span>
                <span className="value">{kycData.businessName}</span>
              </div>
              <div className="review-item">
                <span className="label">Business Type:</span>
                <span className="value">{kycData.businessType.replace(/_/g, ' ')}</span>
              </div>

              <h5>Verification Status</h5>
              <div className="review-item">
                <span className="label">PAN:</span>
                <span className={verificationStatus.pan ? 'verified' : 'pending'}>
                  {verificationStatus.pan ? '✅ Verified' : '⏳ Pending'}
                </span>
              </div>
              <div className="review-item">
                <span className="label">GST:</span>
                <span className={verificationStatus.gst ? 'verified' : 'pending'}>
                  {verificationStatus.gst ? '✅ Verified' : '○ Optional'}
                </span>
              </div>
              <div className="review-item">
                <span className="label">Bank:</span>
                <span className={verificationStatus.bank ? 'verified' : 'pending'}>
                  {verificationStatus.bank ? '✅ Verified' : '⏳ Pending'}
                </span>
              </div>

              <h5>Address</h5>
              <div className="review-item">
                <span className="value">{kycData.registeredAddress}</span>
              </div>
              <div className="review-item">
                <span className="value">{kycData.city}, {kycData.state} - {kycData.pincode}</span>
              </div>

              <div className="submit-warning">
                <p>⚠️ Please review all information carefully before submitting.</p>
                <p>Once submitted, your KYC will be reviewed by our team within 24-48 hours.</p>
              </div>

              <button 
                onClick={submitForReview}
                disabled={loading || !verificationStatus.pan || !verificationStatus.bank}
                className="btn btn-success btn-lg"
              >
                {loading ? 'Submitting...' : 'Submit for Review'}
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="kyc-form-container">
      <h2>Complete Your KYC</h2>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="progress-bar">
        {[1, 2, 3, 4, 5, 6].map(step => (
          <div 
            key={step}
            className={`progress-step ${currentStep >= step ? 'active' : ''} ${currentStep === step ? 'current' : ''}`}
          >
            <div className="step-number">{step}</div>
            <div className="step-label">
              {step === 1 && 'Business'}
              {step === 2 && 'PAN & GST'}
              {step === 3 && 'Address'}
              {step === 4 && 'Bank'}
              {step === 5 && 'Documents'}
              {step === 6 && 'Review'}
            </div>
          </div>
        ))}
      </div>

      <div className="form-container">
        {renderStep()}
      </div>

      <div className="navigation-buttons">
        {currentStep > 1 && (
          <button onClick={prevStep} className="btn btn-secondary">
            ← Previous
          </button>
        )}
        {currentStep < 6 && (
          <button onClick={nextStep} className="btn btn-primary">
            Next →
          </button>
        )}
      </div>
    </div>
  );
};

export default KYCForm;
